import { alpha } from '@mui/material/styles'
import { TrackStatus } from '../../dashboard/tracks/trackTransitions'
import { advancedColors, plannerBar } from '../../theme'
import { colorForOption } from './categories'

export type TagStatus = TrackStatus | 'planned'

// Solid only while running; dashed only for "planned, never started"; `pending` is intake or not yet started.
type TagAppearance = 'plain' | 'planned' | 'pending' | 'running' | 'completed' | 'closed'

const APPEARANCE_BY_STATUS: Record<TagStatus, TagAppearance> = {
  planned: 'planned',
  intake: 'pending',
  scheduled: 'pending',
  active: 'running',
  stalled: 'running',
  completed: 'completed',
  closed: 'closed',
}

type TagFill = 'solid' | 'shaded' | 'faded'

const STYLE_BY_APPEARANCE: Record<TagAppearance, { fill: TagFill; outline: 'none' | 'solid' | 'dashed' }> = {
  plain: { fill: 'shaded', outline: 'none' },
  planned: { fill: 'faded', outline: 'dashed' },
  pending: { fill: 'shaded', outline: 'solid' },
  running: { fill: 'solid', outline: 'none' },
  completed: { fill: 'faded', outline: 'none' },
  closed: { fill: 'faded', outline: 'none' },
}

const FILL_ALPHA: Record<TagFill, number> = {
  solid: plannerBar.fillAlpha.running,
  shaded: plannerBar.fillAlpha.pending,
  faded: plannerBar.fillAlpha.faded,
}

const STAGE_TAGS: Partial<Record<TagStatus, string>> = { intake: 'Intake', scheduled: 'Scheduled' }

const BORDER_STYLE = { solid: 'solid', dashed: 'dashed', late: 'dashed' } as const

type TagLookInput = {
  option: string
  status?: TagStatus
  endDue: boolean
  late: boolean
  acceptedAt?: number
}

export const tagLook = ({ option, status, endDue, late, acceptedAt }: TagLookInput) => {
  const palette = colorForOption(option)
  const ink = endDue ? advancedColors.error : palette.fg
  const appearance = status ? APPEARANCE_BY_STATUS[status] : 'plain'
  const { fill: fillKind, outline } = STYLE_BY_APPEARANCE[appearance]
  const contentOpacity = fillKind === 'faded' ? plannerBar.fadedContentOpacity : 1
  const fill = endDue ? advancedColors.errorLight : alpha(palette.bg, FILL_ALPHA[fillKind])
  const acceptedPct = acceptedAt === undefined ? null : `${acceptedAt * 100}%`
  const outlineKind = late ? 'late' : outline
  const outlineColor = late ? advancedColors.error : alpha(ink, contentOpacity)

  return {
    appearance,
    fillKind,
    outlineKind,
    ink,
    contentOpacity,
    acceptedPct,
    background:
      acceptedPct && appearance === 'running'
        ? `linear-gradient(to right, ${alpha(palette.bg, FILL_ALPHA.faded)} ${acceptedPct}, ${fill} ${acceptedPct})`
        : fill,
    border: outlineKind === 'none' ? 'none' : `1px ${BORDER_STYLE[outlineKind]} ${outlineColor}`,
    stageTag: status && STAGE_TAGS[status],
  }
}
